import React, { useEffect, useId, useState } from 'react';
import Dialog from '../../common/Dialog';
import {
  PatientLifecycleService,
  admitErrorMessage,
  toIsoDate,
} from '../../../services/patientmr/patientLifecycle.service';
import type { AdmitPatientResponse, AdmitWarning } from '../../../dtos/admin/patient.dto';

export interface AdmitDialogProps {
  open: boolean;
  patientId: string;
  onCancel: () => void;
  /** Called after a successful admit (also when there are warnings) once the user closes the dialog */
  onAdmitted: (result: AdmitPatientResponse) => void;
}

/** Human-readable labels for SOC checklist flags returned in Admit warnings. */
const CHECKLIST_ITEM_LABELS: Record<string, string> = {
  referralReceived: 'Referral',
  demographicsComplete: 'Demographics',
  insuranceVerified: 'Insurance verified',
  careTeamAssigned: 'Provider assigned',
  socAssessmentComplete: 'SOC assessment',
  carePlansComplete: 'Care plan',
  physicianOrderComplete: 'Physician order signed',
  priorAuthComplete: 'Prior authorization',
  consentObtained: 'Consent obtained',
  dmeConfirmed: 'DME confirmed',
  emergencyPlanOnFile: 'Emergency plan on file',
  hhaxSetupComplete: 'HHAX setup (EVV)',
};

export function describeAdmitWarning(warning: AdmitWarning): string {
  if (warning.code === 'SOC_CHECKLIST_INCOMPLETE') {
    const items = (warning.items || []).map((key) => CHECKLIST_ITEM_LABELS[key] ?? key);
    const prefix = warning.discipline ? `${warning.discipline} onboarding checklist incomplete` : 'Onboarding checklist incomplete';
    return items.length > 0 ? `${prefix}: ${items.join(', ')}` : prefix;
  }
  return warning.message || warning.code;
}

/**
 * Admit a pending_admission patient (SOC-05): sets ACTIVE and the SOC date. An incomplete SOC
 * checklist never blocks Admit — the server returns warnings, which are shown after success.
 */
const AdmitDialog: React.FC<AdmitDialogProps> = ({ open, patientId, onCancel, onAdmitted }) => {
  const baseId = useId();
  const [socDate, setSocDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AdmitPatientResponse | null>(null);

  useEffect(() => {
    if (!open) return;
    setSocDate(toIsoDate(new Date()));
    setBusy(false);
    setError(null);
    setResult(null);
  }, [open]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!socDate || busy) return;
    setBusy(true);
    setError(null);
    try {
      const response = await PatientLifecycleService.admit(patientId, socDate);
      if (response.warnings.length > 0) {
        setResult(response);
      } else {
        onAdmitted(response);
      }
    } catch (err) {
      setError(admitErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const titleId = `${baseId}-title`;
  const dateId = `${baseId}-soc`;

  if (result) {
    return (
      <Dialog open={open} onClose={() => onAdmitted(result)} labelledBy={titleId}>
        <div className="text-left">
          <h3 id={titleId} className="text-lg font-semibold text-slate-800 pr-6">
            Patient admitted
          </h3>
          {result.activatedDisciplines.length > 0 && (
            <p className="mt-1 text-sm text-slate-600">Activated: {result.activatedDisciplines.join(', ')}</p>
          )}
          <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" role="status">
            <p className="font-medium">Admitted with open onboarding items</p>
            <ul className="mt-1 list-disc pl-5 space-y-0.5" data-testid="admit-warnings">
              {result.warnings.map((warning, index) => (
                <li key={`${warning.code}-${warning.discipline ?? ''}-${index}`}>{describeAdmitWarning(warning)}</li>
              ))}
            </ul>
          </div>
          <div className="mt-5 flex justify-end">
            <button
              type="button"
              onClick={() => onAdmitted(result)}
              className="px-4 py-2 rounded-md text-sm bg-slate-800 hover:bg-slate-700 text-white"
            >
              Done
            </button>
          </div>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={() => !busy && onCancel()} labelledBy={titleId}>
      <form onSubmit={handleSubmit} className="text-left" noValidate>
        <h3 id={titleId} className="text-lg font-semibold text-slate-800 pr-6">
          Admit patient
        </h3>
        <p className="mt-1 text-sm text-slate-600">
          Admitting sets the patient to Active and starts care for each accepted referral. An incomplete
          onboarding checklist does not block admission.
        </p>
        <label htmlFor={dateId} className="block mt-4 text-sm font-medium text-slate-700">
          Start of care date <span className="text-red-600" aria-hidden="true">*</span>
        </label>
        <input
          id={dateId}
          type="date"
          value={socDate}
          onChange={(e) => setSocDate(e.target.value)}
          disabled={busy}
          required
          className="mt-1 w-full rounded-md border border-slate-300 p-2 text-sm"
        />
        {error && (
          <p role="alert" className="mt-3 text-sm text-red-700">
            {error}
          </p>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2 rounded-md text-sm border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || !socDate}
            className="px-4 py-2 rounded-md text-sm bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
          >
            {busy ? 'Admitting…' : 'Admit'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

export default AdmitDialog;
